import { useMutation, useQuery } from '@apollo/client';
import React, { useState } from 'react';
import Modal, { ModalBody, ModalFooter } from '../../components/Modal';
import { Flex, Text } from 'rebass';
import { Label, Select } from '../../components/Form';
import Button from '../../components/Button/Button';
import PropTypes from 'prop-types';
import useApolloClient from '../../hooks/useApolloClient';
import useMode from '../../hooks/useMode';
import delay from '../../helpers/delay';
import { downloadFile } from '../../helpers/download';
import { IdArrayInput } from '../../hooks/useTablePages';
import useNotify from '../../hooks/useNotify';
import { format } from 'date-fns';
import slugify from 'slugify';
import ProgressBar from '../../components/Progress/ProgressBar';
import { gql } from '@/modes/company/gql';

type SendNoticeModalProps = {
  onClose: () => void;
  selectedIds: IdArrayInput;
};

const GET_NOTICES = gql(`
  query GetNotices {
    notices(where: { archived: null }) {
      id
      type
    }
  }
`);

const GET_NOTICE_JOB = gql(`
  query GetNoticeJob($noticeJobId: ID!) {
    noticeJob(noticeJobId: $noticeJobId) {
      id
      status
      url
      errorMessage
      noticesCount
      noticesComplete
      mailCount
      textCount
      emailCount
      created
      updated
    }
  }
`);

const CREATE_NOTICE_PDF = gql(`
  mutation SendNotices($noticeId: ID!, $assemblyIds: [ID!]!) {
    sendNotices(noticeId: $noticeId, assemblyIds: $assemblyIds) {
      id
    }
  }
`);

type NoticeJobStatus = 'PENDING' | 'RUNNING' | 'COMPLETE' | 'FAILED';

type NoticeJob = {
  id: string;
  status: NoticeJobStatus;
  url: string | null;
  errorMessage: string | null;
  noticesCount: number | null;
  noticesComplete: number;
  emailCount: number;
  mailCount: number;
  textCount: number;
  created: number;
  updated: number;
};

const SendNoticeModal: React.FC<SendNoticeModalProps> = ({
  onClose,
  selectedIds,
}) => {
  const [noticeId, setNoticeId] = useState<string>('');
  const { data, loading } = useQuery<{
    notices: { id: string; type: string }[];
  }>(GET_NOTICES, {
    fetchPolicy: 'network-only',
  });

  const { showError, showSuccess } = useNotify();

  const [generating, setGenerating] = useState<boolean>(false);
  const [progress, setProgress] = useState<NoticeJob | null>(null);

  const [sendNotices, { data: pdfData, loading: pdfLoading, error: pdfError }] =
    useMutation<{
      sendNotices: {
        id: string;
      };
    }>(CREATE_NOTICE_PDF, {
      onError: (error) => {
        console.error('Error creating notice PDF', error);
      },
    });

  const apollo = useApolloClient(useMode());
  const updateStatus = async (noticeJobId: string): Promise<NoticeJob> => {
    const job: NoticeJob = (
      await apollo.query<{
        noticeJob: NoticeJob;
      }>({
        query: GET_NOTICE_JOB,
        variables: {
          noticeJobId,
        },
        fetchPolicy: 'no-cache',
      })
    ).data.noticeJob;

    setProgress(job);

    if (job.status === 'RUNNING' || job.status === 'PENDING') {
      await delay(2000);
      return updateStatus(noticeJobId);
    }
    return job;
  };

  const createNotices = async () => {
    setGenerating(true);

    const createResult = await sendNotices({
      variables: {
        noticeId,
        assemblyIds: selectedIds,
      },
    });

    try {
      const noticeJobId = createResult.data?.sendNotices.id;
      if (!noticeJobId) {
        throw new Error('Failed to create notice job');
      }

      const result = await updateStatus(noticeJobId);
      if (result.status !== 'COMPLETE') {
        let message = `Notice PDF job status: ${result.status}`;
        if (result.errorMessage) {
          message = result.errorMessage;
        }
        throw new Error(message);
      }
      if (!result.url) {
        throw new Error('No file url returned');
      }

      if (result.mailCount > 0) {
        const fileResponse = await fetch(result.url);
        if (fileResponse.status !== 200) {
          showError(`Error downloading notice PDF ${result.url}`);
          return;
        }

        const name =
          data?.notices.find((notice) => notice.id === noticeId)?.type ??
          'notices';
        const date = format(new Date(), 'yyyy-MM-dd');
        const fileName = `${slugify(name, { strict: true })}_${date}.pdf`;

        const blob = await fileResponse.blob();
        downloadFile(fileName, blob);
      }
      if (result.emailCount > 0) {
        showSuccess(`${result.emailCount} notices emailed`);
      }

      onClose();
    } catch (e) {
      console.error(e);
      if (e instanceof Error) {
        showError(e.message);
      }
    } finally {
      setGenerating(false);
    }
  };

  // console.log(
  //   `${progress.noticesComplete} / ${progress.noticesCount}`,
  //   format(progress.updated, 'h:mm:ss')
  // );

  return (
    <Modal title="Create Notice" onClose={onClose}>
      <ModalBody>
        <Flex p={3} flexDirection="column">
          <Text variant="body1">
            Generate{' '}
            {noticeId && data
              ? '"' +
                data.notices.find((notice) => notice.id === noticeId)?.type +
                '" '
              : ''}
            letters for {selectedIds.length.toLocaleString()} assemblies
          </Text>
          <Label mt={5}>Notice</Label>
          {data && (
            <Select
              width="100%"
              value={noticeId}
              name="state"
              onChange={(e) => {
                setNoticeId(e.target.value);
              }}
            >
              <option value="">Select Notice</option>
              {data.notices.map(({ id, type }) => (
                <option value={id} key={id}>
                  {type}
                </option>
              ))}
            </Select>
          )}
          {progress && !!progress.noticesCount && (
            <>
              <Label mt={5}>Progress</Label>
              <ProgressBar
                complete={progress.noticesComplete}
                total={progress.noticesCount}
              />
            </>
          )}
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          disabled={!noticeId || generating}
          icon="file-download"
          onClick={createNotices}
        >
          {generating ? 'Creating...' : 'Create'}
        </Button>
        <Button variant="outline" icon="slash-circle" onClick={onClose}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};

SendNoticeModal.propTypes = {
  onClose: PropTypes.any,
  selectedIds: PropTypes.any.isRequired,
};

export default SendNoticeModal;
